import { useTranslation } from 'react-i18next';

import { ERROR_MESSAGE_KEYS, RequestError } from '../utils/apiError';

/**
 * Resolve a caught API failure to a translated, user-facing string.
 *
 * A hook rather than a plain function because it needs `t`, and passing
 * i18next's TFunction into a `(key: string) => string` parameter does not
 * typecheck under strict mode. Keeping `t` inside the hook avoids the question.
 *
 * Precedence: a known backend code wins (that is the translated copy), then the
 * server's own message (readable English for a code this app does not know yet),
 * then the caller's fallback key (for a thrown string or an empty Error).
 */
export function useApiErrorMessage() {
  const { t } = useTranslation();

  return (error: unknown, fallbackKey: string): string => {
    if (error instanceof RequestError && error.code) {
      const key = ERROR_MESSAGE_KEYS[error.code];
      if (key) return t(key);
    }
    if (error instanceof Error && error.message) return error.message;
    return t(fallbackKey);
  };
}
